<template>
	<div class="index-bar">
		<div ref="contentScroll" class="index-bar__content" :style="{ maxHeight: height }" @scroll.passive="onScroll">
			<section
				v-for="group in options"
				:key="group.name"
				:id="getGroupId(group.name)"
				class="index-bar__group"
			>
				<header v-if="group.children && group.children.length > 0" class="index-bar__group-title">
					<div>{{ group.name }}</div>
					<slot v-if="group.name === currentLetter" name="title" :letter="group.name"></slot>
				</header>
				<component
					:is="item.href ? 'a' : 'div'"
					v-for="item in group.children"
					:key="item.id || item.companyId"
					:href="item.href || undefined"
					class="index-bar__item"
					@click="onItemWrapperClick(item)"
				>
					<slot name="item" :item="item" :group="group">
						{{ item.text || item.websiteName }}
					</slot>
				</component>
			</section>
		</div>

		<aside
			ref="sidebar"
			class="index-bar__sidebar"
			@mousedown.prevent="onMouseDown"
			@mouseup.prevent="onMouseUp"
			@mouseleave.prevent="onMouseUp"
		>
			<button
				v-for="(letter, index) in indexList"
				:key="letter"
				class="index-bar__index"
				:class="{ 'index-bar__index--active': activeIndex === index }"
				type="button"
				@mouseenter="onSidebarHover(letter, index)"
				@click.stop.prevent="scrollToLetter(letter)"
			>
				{{ letter }}
			</button>
		</aside>

		<div v-if="showToast && currentLetter" class="index-bar__toast">
			{{ currentLetter }}
		</div>
	</div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const props = defineProps({
	options: {
		type: Array,
		default: () => [],
	},
	height: {
		type: String,
		default: '60vh',
	},
	swipeable: {
		type: Boolean,
		default: true,
	},
	showToast: {
		type: Boolean,
		default: false,
	},
	indexList: {
		type: Array,
		default: () => ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', '#'],
	},
});

const emit = defineEmits(['item-click', 'scroll-to-letter', 'click']);

const scrollContainer = ref(null);
const sidebarRef = ref(null);
const activeIndex = ref(-1);
const currentLetter = ref('');
const groupTopOffsets = ref([]);
const isMouseDown = ref(false);
const updateIndex = ref(true);

const contentScroll = scrollContainer;
const sidebar = sidebarRef;

const handleResize = () => {
	updateGroupOffsets();
};

const getGroupId = name => {
	return name === '#' ? 'index-group-other' : `index-group-${name}`;
};

const updateGroupOffsets = async () => {
	await nextTick();
	const container = contentScroll.value;
	if (!container) {
		groupTopOffsets.value = [];
		return;
	}

	const offsets = [];
	props.options.forEach(group => {
		if (!group.children || !group.children.length) return;
		const selectorId = getGroupId(group.name);
		const el = container.querySelector(`#${selectorId}`);
		if (el) {
			offsets.push({ letter: group.name, top: el.offsetTop });
		}
	});

	groupTopOffsets.value = offsets.sort((a, b) => a.top - b.top);
};

watch(
	() => props.options,
	() => {
		nextTick(() => {
			updateGroupOffsets();
			const firstValidGroup = props.options.find(group => group.children && group.children.length > 0);
			if (firstValidGroup) {
				scrollToLetter(firstValidGroup.name);
			}
		});
	},
	{ immediate: true }
);

onMounted(() => {
	if (typeof window !== 'undefined') {
		window.addEventListener('resize', handleResize);
	}
	updateGroupOffsets();
});

onBeforeUnmount(() => {
	if (typeof window !== 'undefined') {
		window.removeEventListener('resize', handleResize);
	}
});

const scrollToLetter = letter => {
	const targetGroup = props.options.find(group => group.name === letter);
	if (!targetGroup || !targetGroup.children?.length) return;
	currentLetter.value = letter;
	const index = props.indexList.indexOf(letter);

	if (index !== -1 && updateIndex.value) {
		activeIndex.value = index;
	}

	const container = contentScroll.value;
	if (!container) return;

	const selectorId = getGroupId(letter);
	const el = container.querySelector(`#${selectorId}`);
	if (!el) return;

	container.scrollTo({
		top: el.offsetTop,
		behavior: 'smooth',
	});

	emit('click', { letter, group: targetGroup });
};

const onScroll = () => {
	const container = contentScroll.value;
	if (!container) return;
	const scrollTop = container.scrollTop;
	const offsets = groupTopOffsets.value;
	if (!offsets.length) return;

	let letter = null;
	if (updateIndex.value) {
		for (let i = offsets.length - 1; i >= 0; i--) {
			if (offsets[i].top <= scrollTop + 15) {
				letter = offsets[i].letter;
				break;
			}
		}
	}

	if (letter && currentLetter.value !== letter) {
		currentLetter.value = letter;
		const idx = props.indexList.indexOf(letter);
		if (idx !== -1 && updateIndex.value) {
			activeIndex.value = idx;
		}
		emit('scroll-to-letter', { letter });
	}
};

const onItemClick = item => {
	emit('item-click', item);
};

const onItemWrapperClick = item => {
	if (item?.href) {
		emit('click', item);
		return;
	}
	onItemClick(item);
};

const onMouseDown = () => {
	isMouseDown.value = true;
};

const onMouseUp = () => {
	isMouseDown.value = false;
};

const onSidebarHover = (letter, index) => {
	if (!props.swipeable || !isMouseDown.value) return;
	updateIndex.value = false;
	scrollToLetter(letter);
	activeIndex.value = index;
	setTimeout(() => {
		updateIndex.value = true;
	}, 300);
};
</script>

<style scoped>
.index-bar {
	position: relative;
	display: flex;
	width: 100%;
	height: 100%;
	overflow: hidden;
}

.index-bar__content {
	flex: 1;
	overflow-y: auto;
	box-sizing: border-box;
}

.index-bar__group {
	width: 100%;
}

.index-bar__group-title {
	position: sticky;
	top: 0;
	padding: 0 8px;
	background: #f0f6ff;
	height: 24px;
	line-height: 24px;
	font-size: 16px;
	font-weight: bold;
	display: flex;
	align-items: center;
	color: #333333;
	justify-content: space-between;
}

.index-bar__item {
	display: block;
	padding: 12px 16px 12px 8px;
	font-size: 16px;
	color: #323233;
	background-color: #fff;
	border-bottom: 1px solid #f5f5f5;
	cursor: pointer;
	text-decoration: none;
}

.index-bar__item:last-child {
	border-bottom: none;
}

.index-bar__sidebar {
	position: fixed;
	top: 50%;
	right: 3px;
	transform: translateY(-50%);
	z-index: 10;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 4px;
	padding: 7.5px 0 8px;
	background: #ebf0fa;
	border-radius: 30px;
}

.index-bar__index {
	width: 20px;
	height: 16px;
	line-height: 16px;
	text-align: center;
	font-size: 12px;
	border-radius: 120px;
	color: #595959;
	border: none;
	background: transparent;
	padding: 0;
	cursor: pointer;
}

.index-bar__index--active {
	background-color: #687df7;
	font-weight: bold;
	color: #fff;
}

.index-bar__toast {
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: 60px;
	height: 60px;
	background-color: rgba(0, 0, 0, 0.7);
	color: #fff;
	font-size: 20px;
	font-weight: bold;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 9999;
}
</style>
